import { action, computed, observable, makeObservable, observe, toJS } from 'mobx'
import { ICollectionLink, IDepartment } from './collections.interfaces'
import {
  links,
  reorderCollectionTitle,
  reorderCollectionOrder,
  REORDER_COLLECTION_HANDLE,
  ReorderProduct,
  enableReorderCollection,
  customerOrders,
  reorderCollectionBanner,
  weeklyMenus,
  departments
} from './collections.data'
import { cartStore } from '../cart'
import { collectionListStore } from './collection-list/collection-list.store'
import { IReorderItem, ICustomerOrder } from '../../interfaces/orders.interfaces'
import { yfSubscriptionPrivateService } from '../../services'
import { customerStore } from '../../stores'
import { IShopifyLineItemRestfulResponse } from '../../interfaces/shopify/checkout.interfaces'
import { filterBarStore } from './filter-bar'
import { retrieveFromWindow } from '../../utils'

class CollectionsStore {
  @observable activeDepartment: IDepartment = departments[0]
  @observable shopLinks: ICollectionLink[] = links
  @observable activeLink: ICollectionLink | null = null
  @observable previousRotationalMenu: ICollectionLink | null = null
  @observable rotationalMenu: ICollectionLink | null = null
  @observable links: ICollectionLink[] = this.getLinks()
  @observable weeklyMenus: ICollectionLink[] = weeklyMenus
  @observable selectedDeliveryWeek: string | null = null
  @observable customerPreferredDeliveryDay: string | null = retrieveFromWindow('customerPreferredDeliveryDay')
  @observable customerOverrideDeliveryDay: string | null = retrieveFromWindow('customerOverrideDeliveryDay')
  @observable customerOverrideDeliveryRegion: string | null = retrieveFromWindow('customerOverrideDeliveryRegion')
  /**
   * Note: `favouriteOrderIds` is just an array of tags from the Customer as defined in Shopify Admin.
   * Those tags may or may not include the favourite order id.
   */
  @observable favouriteOrderIds: string[] | null = null
  @observable favouriteOrders: any[] = []
  @observable customerId = customerStore.id

  constructor() {
    makeObservable(this)

    if (toJS(this.shopLinks).length > 0) {
      this.activeLink = this.shopLinks[0]
    }

    // this.getFavouriteOrderIds()

    const selectedDeliveryDate = JSON.parse(sessionStorage.getItem('selectedDeliveryDate'))
    if (selectedDeliveryDate && selectedDeliveryDate.week) {
      this.selectedDeliveryWeek = selectedDeliveryDate.week
    }

    observe(this, 'selectedDeliveryWeek', () => {
      const currentWeeklyMenu = this.weeklyMenus.find((menu: ICollectionLink): boolean => menu.deliveryWeek === this.selectedDeliveryWeek)
      if (currentWeeklyMenu) {
        const products = collectionListStore.collections(currentWeeklyMenu.handle)
        filterBarStore.organizeFilters(products)
      }
    })
  }

  @action
  setFavouriteOrderIds = (orderIds: string[] | null): void => {
    this.favouriteOrderIds = (orderIds || []).filter((id) => !isNaN(parseInt(id))) // filter out the tags are not likely a favourite id
  }

  async getFavouriteOrderIds() {
    if (!this.customerId) {
      return
    }
    const responseData = await yfSubscriptionPrivateService.getFavouriteOrder({ customerId: this.customerId.toString() })
    this.setFavouriteOrderIds(responseData && responseData.data.customer.tags)
    this.getFavouriteOrders(this.customerOrdersWithFavTag, this.favouriteOrderIds)
  }

  getLinks(): ICollectionLink[] {
    // if (this.showReorderCollection) {
    //   const shopLinks = this.shopLinks

    //   // Splice will insert at the index specified in reorderCollectionOrder
    //   shopLinks.splice(reorderCollectionOrder, 0, this.reorderCollectionLink)

    //   // remove the previous rotational menu if it exists
    //   if (this.previousRotationalMenu) {
    //     shopLinks.splice(shopLinks.indexOf(this.previousRotationalMenu), 1)
    //   }

    //   // add the new rotational menu if it exists
    //   if (this.rotationalMenu) {
    //     shopLinks.push(this.rotationalMenu)
    //   }

    //   return shopLinks
    // }

    return this.shopLinks
  }

  @action
  updateMenuForDelivery = (week: String): void => {
    const menu = weeklyMenus.find((menu) => menu.deliveryWeek === week)

    if (this.rotationalMenu) {
      this.previousRotationalMenu = this.rotationalMenu
    }

    this.rotationalMenu = menu || null

    if (this.rotationalMenu) {
      this.links = [...this.links, this.rotationalMenu]
    }

    // remove the previous rotational menu if it exists
    if (this.previousRotationalMenu) {
      const idx = this.links.indexOf(this.previousRotationalMenu)
      if (idx > -1) {
        this.links.splice(idx, 1)
      }
    }
  }

  getRotationalMenu(): ICollectionLink {
    return this.rotationalMenu
  }

  isFavouriteOrder(orderId: string): boolean {
    return this.favouriteOrderIds && this.favouriteOrderIds.length > 0 ? this.favouriteOrderIds.includes(orderId) : undefined
  }

  /**
   * Returns an array of all the collection link objects,
   * including the reorder collection if enabled.
   */
  @computed get allLinks(): ICollectionLink[] {
    const allLinks = this.shopLinks

    if (this.rotationalMenu) {
      allLinks.push(this.rotationalMenu)
    }

    /**
     * If we are displaying the reorder collection, then return the reorder
     * collection as the specified index. Otherwise exclude it entirely.
     */
    if (this.showReorderCollection) {
      const allLinksWithReorder = [...allLinks]
      allLinksWithReorder.splice(reorderCollectionOrder, 0, this.reorderCollectionLink)

      return allLinksWithReorder
    }

    return allLinks
  }

  @computed get showReorderCollection(): boolean {
    return this.validReorderItems.length && enableReorderCollection
  }

  @computed get reorderCollectionLink(): ICollectionLink {
    return {
      title: this.reorderCollectionDisplayTitle,
      handle: REORDER_COLLECTION_HANDLE,
      level: 1,
      hasSubLinks: false,
      banner: reorderCollectionBanner,
      week: -1,
      year: -1
    }
  }

  @computed get reorderCollectionDisplayTitle(): string {
    return this.reOrderableOrder && this.reOrderableOrder.isFavourite ? 'Favourite Order' : reorderCollectionTitle
  }

  /**
   * Returns true if there is an item in the reorder that is not currently in the cart,
   * or exists in the cart but has a smaller quantity than the reorder
   */
  @computed get canAddReorderToCart(): boolean {
    let canAdd = false

    this.validReorderItems.forEach((item) => {
      const id = item.product.id
      if (cartStore.items.has(id)) {
        if (cartStore.items.get(id).quantity < item.quantity) {
          canAdd = true
        }
      } else {
        canAdd = true
      }
    })

    return canAdd
  }

  /**
   * Returns orders with a flag `isFavourite`. An order is a favourite if the customer tag contains the order id.
   */
  @computed get customerOrdersWithFavTag(): ICustomerOrder[] {
    return customerOrders.map((order) => ({
      ...order,
      isFavourite: this.favouriteOrderIds && this.favouriteOrderIds.includes(order.id)
    }))
  }

  /**
   * Returns orders that match the given favourites.
   *
   * @param orders List of orders
   * @param favouriteOrderIds List of customer tags that may contain favourite order ids
   */
  async getFavouriteOrders(orders: ICustomerOrder[], favouriteOrderIds: null | string[]) {
    if (orders.length === 0 || !favouriteOrderIds || favouriteOrderIds.length === 0) {
      this.setFavouriteOrders([])
    }

    const currPageFavouriteOrders = orders.filter((order) => favouriteOrderIds.includes(order.id))

    if (currPageFavouriteOrders.length > 0) {
      this.setFavouriteOrders(currPageFavouriteOrders)
    } else if (favouriteOrderIds && favouriteOrderIds.length > 0) {
      // When favourite order is not part of current page of order histories
      const favouriteOrderResponse = await yfSubscriptionPrivateService.getOrderByIdRestfulApi(
        favouriteOrderIds[0].toString(),
        this.customerId.toString()
      )
      const favouriteOrder = favouriteOrderResponse.order
      const deliveryDayAttribute = favouriteOrder.note_attributes.find((attribute) => attribute.name === 'delivery_day')
      const mappedFavouriteOrder = {
        id: favouriteOrder.id,
        name: favouriteOrder.name,
        customer: {
          id: favouriteOrder.customer.id
        },
        customer_url: `/account/orders/${favouriteOrder.token}`,
        created_at: new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'long',
          day: '2-digit'
        })
          .format(new Date(favouriteOrder.created_at))
          .replace(/,/g, ''),
        fulfillment_status: favouriteOrder.fulfillment_status,
        attributes: {
          delivery_day: (deliveryDayAttribute && deliveryDayAttribute.value.replace(/[\(\)]/g, '')) || ''
        },
        total_price: favouriteOrder.total_price,
        isFavourite: true,
        line_items: [
          ...favouriteOrder.line_items.map((lineItem: IShopifyLineItemRestfulResponse) => {
            const product = collectionListStore.displayedProducts.find((product) => product.id === lineItem.product_id)
            return {
              product: {
                variantId: lineItem.variant_id,
                id: lineItem.product_id,
                available: product ? product.available : false
              },
              quantity: lineItem.quantity
            }
          })
        ]
      }

      this.setFavouriteOrders([mappedFavouriteOrder])
    }
  }

  @action
  setFavouriteOrders = (favouriteOrders: any[]): void => {
    this.favouriteOrders = favouriteOrders
  }

  @computed get reOrderableOrder(): ICustomerOrder | null {
    if (this.customerOrdersWithFavTag.length === 0) {
      return null
    }

    const favouriteOrder = this.customerOrdersWithFavTag.find((order) => order.isFavourite)
    const lastOrder = this.customerOrdersWithFavTag.find((order) => order.isLastOrder)

    if (!favouriteOrder) {
      return lastOrder
    }

    return favouriteOrder
  }

  /**
   * Returns an array of Reorder Items that exist in a least one active collection
   * Note: does not check product.available
   */
  @computed get validReorderItems(): ReorderProduct[] {
    if (!this.reOrderableOrder) {
      return []
    }

    return this.reOrderableOrder.line_items
  }

  get reOrderableOrderItems(): ReorderProduct[] {
    if (this.validReorderItems.length === 0) {
      return null
    }

    return this.getReOrderableOrderItems(this.validReorderItems)
  }

  @computed get unavailableOrderItems(): ReorderProduct[] {
    return this.getUnavailableOrderItems(this.validReorderItems)
  }

  getUnavailableOrderItems(orderItems: IReorderItem[]): ReorderProduct[] {
    return orderItems.filter((item) => !collectionListStore.displayedProducts.map((p) => p.id).includes(item.product.id))
  }

  get outOfStockOrderItems(): ReorderProduct[] {
    return this.getOutOfStockOrderItems(this.validReorderItems)
  }

  getOutOfStockOrderItems(orderItems: IReorderItem[]): ReorderProduct[] {
    return this.getItemsInActiveCollection(orderItems).filter((item) => !item.product.available)
  }

  getItemsInActiveCollection(orderItems: IReorderItem[]): ReorderProduct[] {
    return orderItems.filter((item) => collectionListStore.displayedProducts.map((p) => p.id).includes(item.product.id))
  }

  getReOrderableOrderItems(orders: ReorderProduct[]): ReorderProduct[] {
    const itemsInActiveCollection = this.getItemsInActiveCollection(orders)
    if (itemsInActiveCollection.length === 0) {
      return []
    }

    return this.getAvailableOrderItems(itemsInActiveCollection)
  }

  /**
   * Returns an array of valid Reorder Items that are available
   */
  @computed get availableReorderItems(): ReorderProduct[] {
    return this.getAvailableOrderItems(this.validReorderItems)
  }

  getAvailableOrderItems(orderItems: IReorderItem[]): ReorderProduct[] {
    return orderItems.filter((item) => item.product.available)
  }

  /**
   * Returns a float/decimal of the cost of the available reorder items
   */
  @computed get reorderCost(): number {
    return this.availableReorderItems.reduce((accumulator: number, item: ReorderProduct) => {
      return accumulator + item.quantity * item.product.price
    }, 0)
  }

  @computed get currentWeeklyMenu(): ICollectionLink | undefined {
    return this.weeklyMenus.find((menu: ICollectionLink): boolean => menu.deliveryWeek === this.selectedDeliveryWeek)
  }
}

export const collectionsStore = new CollectionsStore()
